import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { cn, formatCurrency } from '../../lib/utils';
import { Search, DollarSign, Users, CreditCard, Clock, Send, MoreHorizontal, Mail, Phone, FileText, Sparkles, Bot, MessageSquare, PhoneCall, CalendarClock, CheckCircle2, Loader2, Eye } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { toast } from 'sonner';
import { useAppMode } from '../../contexts/AppModeContext';

const INITIAL_PATIENTS = [
  { id: 1, name: 'Sarah Johnson', balance: 285.50, lastService: '2026-01-30', daysPast: 15, status: 'current', phone: '(555) 123-4567', email: 'sarah.j@email.com', history: [
    { date: '2026-01-30', desc: 'Office Visit - Copay', charge: 45.00, payment: 0, balance: 45.00 },
    { date: '2026-01-15', desc: 'Lab Work - Patient Resp', charge: 240.50, payment: 0, balance: 240.50 },
  ]},
  { id: 2, name: 'Michael Brown', balance: 450.00, lastService: '2026-01-15', daysPast: 45, status: 'past30', phone: '(555) 234-5678', email: 'mbrown@email.com', history: [
    { date: '2026-01-15', desc: 'Procedure - Deductible', charge: 450.00, payment: 0, balance: 450.00 },
    { date: '2025-12-10', desc: 'Office Visit', charge: 150.00, payment: 150.00, balance: 0 },
  ]},
  { id: 3, name: 'Emily Davis', balance: 1250.00, lastService: '2025-12-20', daysPast: 72, status: 'past60', phone: '(555) 345-6789', email: 'emily.d@email.com', history: [
    { date: '2025-12-20', desc: 'Surgery - Patient Resp', charge: 1250.00, payment: 0, balance: 1250.00 },
  ]},
  { id: 4, name: 'Robert Wilson', balance: 875.00, lastService: '2025-11-10', daysPast: 112, status: 'past90', phone: '(555) 456-7890', email: 'rwilson@email.com', history: [
    { date: '2025-11-10', desc: 'Specialist Consult', charge: 375.00, payment: 0, balance: 375.00 },
    { date: '2025-10-05', desc: 'Lab Work', charge: 500.00, payment: 0, balance: 500.00 },
  ]},
  { id: 5, name: 'Lisa Anderson', balance: 125.00, lastService: '2026-01-28', daysPast: 2, status: 'current', phone: '(555) 567-8901', email: 'lisa.a@email.com', history: [
    { date: '2026-01-28', desc: 'Office Visit - Copay', charge: 125.00, payment: 0, balance: 125.00 },
  ]},
  { id: 6, name: 'James Taylor', balance: 590.00, lastService: '2026-01-10', daysPast: 50, status: 'past30', phone: '(555) 678-9012', email: 'jtaylor@email.com', history: [
    { date: '2026-01-10', desc: 'Imaging - Patient Resp', charge: 590.00, payment: 0, balance: 590.00 },
  ]},
];

const AGING_DATA = [
  { bucket: 'Current', amount: 12500, count: 45 },
  { bucket: '1-30 Days', amount: 8750, count: 28 },
  { bucket: '31-60 Days', amount: 6200, count: 18 },
  { bucket: '61-90 Days', amount: 4100, count: 12 },
  { bucket: '90+ Days', amount: 3450, count: 8 },
];

const COLLECTION_METHODS = [
  { method: 'Online Portal', amount: 18500, percentage: 45 },
  { method: 'Credit Card', amount: 12200, percentage: 30 },
  { method: 'Check', amount: 6100, percentage: 15 },
  { method: 'Cash', amount: 4100, percentage: 10 },
];

const COLORS = ['#10b981', '#f59e0b', '#ef4444', '#dc2626', '#991b1b'];

const AI_RECOMMENDATIONS = [
  { id: 1, type: 'reminder', icon: MessageSquare, title: 'Send SMS Payment Reminders', description: '4 patients have balances 30+ days past due. Automated text reminders have a 62% response rate.', patients: ['Michael Brown', 'James Taylor', 'Emily Davis', 'Robert Wilson'], impact: 'High', action: 'Send SMS to 4 patients' },
  { id: 2, type: 'call', icon: PhoneCall, title: 'Schedule Collection Calls', description: 'Robert Wilson ($875) is 112 days past due. Phone outreach recommended for accounts over 90 days.', patients: ['Robert Wilson'], impact: 'Critical', action: 'Schedule call for 1 patient' },
  { id: 3, type: 'statement', icon: Mail, title: 'Batch Statement Mailing', description: 'Send electronic statements to 3 patients with current balances to prevent aging.', patients: ['Sarah Johnson', 'Lisa Anderson', 'Michael Brown'], impact: 'Medium', action: 'Send statements to 3 patients' },
  { id: 4, type: 'plan', icon: CalendarClock, title: 'Offer Payment Plans', description: 'Emily Davis ($1,250) qualifies for a payment plan. Offering installment options reduces write-offs by 40%.', patients: ['Emily Davis'], impact: 'High', action: 'Create payment plan for 1 patient' },
];

const RevQPatientBilling = () => {
  const { isDemo } = useAppMode();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [patients, setPatients] = useState(INITIAL_PATIENTS);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [modalType, setModalType] = useState(null);
  const [showAiPanel, setShowAiPanel] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('credit_card');
  const [planInstallments, setPlanInstallments] = useState('3');
  const [planFrequency, setPlanFrequency] = useState('monthly');

  const openModal = (patient, type) => {
    setSelectedPatient(patient);
    setModalType(type);
    setPaymentAmount(type === 'payment' ? String(patient.balance) : '');
    setPaymentMethod('credit_card');
    setPlanInstallments('3');
    setPlanFrequency('monthly');
  };

  const closeModal = () => {
    setSelectedPatient(null);
    setModalType(null);
  };

  const handleProcessPayment = () => {
    const amount = parseFloat(paymentAmount);
    if (!amount || amount <= 0) { toast.error('Enter a valid payment amount'); return; }
    if (amount > selectedPatient.balance) { toast.error('Amount exceeds patient balance'); return; }
    setPatients(prev => prev.map(p =>
      p.id === selectedPatient.id ? { ...p, balance: Math.max(0, p.balance - amount) } : p
    ));
    toast.success(`Payment of ${formatCurrency(amount)} processed for ${selectedPatient.name}`);
    closeModal();
  };

  const handleSendStatement = () => {
    toast.success(`Statement sent to ${selectedPatient.name}`, { description: `Email sent to ${selectedPatient.email}` });
    closeModal();
  };

  const handleSetupPlan = () => {
    const installments = parseInt(planInstallments);
    const perPayment = selectedPatient.balance / installments;
    toast.success(`Payment plan created for ${selectedPatient.name}`, {
      description: `${installments} ${planFrequency} payments of ${formatCurrency(perPayment)}`
    });
    closeModal();
  };

  const handleAiAction = (rec) => {
    toast.success(`Action initiated: ${rec.action}`, { description: `Patients: ${rec.patients.join(', ')}` });
  };

  const toggleAiPanel = () => {
    if (!showAiPanel) {
      setAiLoading(true);
      setTimeout(() => setAiLoading(false), 1200);
    }
    setShowAiPanel(!showAiPanel);
  };

  const getStatusBadge = (status) => {
    const configs = {
      current: { label: 'Current', class: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
      past30: { label: '1-30 Days', class: 'bg-amber-50 text-amber-700 border-amber-200' },
      past60: { label: '31-60 Days', class: 'bg-orange-50 text-orange-700 border-orange-200' },
      past90: { label: '90+ Days', class: 'bg-red-50 text-red-700 border-red-200' },
    };
    const config = configs[status] || configs.current;
    return <Badge className={config.class}>{config.label}</Badge>;
  };

  const getImpactBadge = (impact) => {
    const map = { Critical: 'bg-red-50 text-red-700 border-red-200', High: 'bg-amber-50 text-amber-700 border-amber-200', Medium: 'bg-blue-50 text-blue-700 border-blue-200' };
    return <Badge className={map[impact] || map.Medium}>{impact}</Badge>;
  };

  const filteredPatients = patients.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || p.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const totalBalance = patients.reduce((sum, p) => sum + p.balance, 0);
  const patientsWithBalance = patients.filter(p => p.balance > 0).length;

  return (
    <div className="space-y-6" data-testid="patient-billing-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900">Patient Billing</h1>
          <p className="text-slate-500 mt-1">Manage patient balances and collections</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2" onClick={toggleAiPanel} data-testid="ai-analyze-btn">
            <Bot className="h-4 w-4" /> {showAiPanel ? 'Hide AI Analysis' : 'AI Analysis'}
          </Button>
          <Button variant="outline" className="gap-2">
            <Mail className="h-4 w-4" /> Send Statements
          </Button>
          <Button className="bg-black hover:bg-gray-800 gap-2">
            <CreditCard className="h-4 w-4" /> Process Payment
          </Button>
        </div>
      </div>

      {/* AI Analysis Panel */}
      {showAiPanel && (
        <Card className="border-violet-200 bg-gradient-to-r from-violet-50/50 to-white" data-testid="ai-analysis-panel">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-violet-100 flex items-center justify-center">
                <Sparkles className="h-4 w-4 text-violet-600" />
              </div>
              <div>
                <CardTitle className="text-base font-semibold">AI Billing Automation Recommendations</CardTitle>
                <p className="text-xs text-muted-foreground">Analyzed {patients.length} patient accounts</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {aiLoading ? (
              <div className="flex items-center justify-center py-8 gap-2 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" /> Analyzing patient billing data...
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {AI_RECOMMENDATIONS.map((rec) => {
                  const Icon = rec.icon;
                  return (
                    <div key={rec.id} className="rounded-lg border bg-white p-4 space-y-3 hover:shadow-sm transition-shadow" data-testid={`ai-rec-${rec.id}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4 text-violet-600" />
                          <span className="text-sm font-medium">{rec.title}</span>
                        </div>
                        {getImpactBadge(rec.impact)}
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">{rec.description}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-500">{rec.patients.length} patient{rec.patients.length > 1 ? 's' : ''}</span>
                        <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => handleAiAction(rec)} data-testid={`ai-rec-action-${rec.id}`}>
                          <CheckCircle2 className="h-3 w-3" /> {rec.action}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center"><DollarSign className="h-5 w-5 text-slate-600" /></div><div><p className="text-sm text-muted-foreground">Total Patient A/R</p><p className="text-xl font-bold">{formatCurrency(totalBalance)}</p></div></div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center"><Users className="h-5 w-5 text-blue-600" /></div><div><p className="text-sm text-muted-foreground">Patients w/ Balance</p><p className="text-xl font-bold">{patientsWithBalance}</p></div></div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-lg bg-emerald-50 flex items-center justify-center"><CreditCard className="h-5 w-5 text-emerald-600" /></div><div><p className="text-sm text-muted-foreground">Collected MTD</p><p className="text-xl font-bold text-emerald-600">{formatCurrency(28500)}</p></div></div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-lg bg-amber-50 flex items-center justify-center"><Clock className="h-5 w-5 text-amber-600" /></div><div><p className="text-sm text-muted-foreground">Avg Days to Pay</p><p className="text-xl font-bold">24 days</p></div></div></CardContent></Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="text-lg font-semibold">Patient A/R Aging</CardTitle></CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={AGING_DATA}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="bucket" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <YAxis tickFormatter={(v) => `$${v/1000}k`} tick={{ fontSize: 12 }} stroke="#94a3b8" />
                  <Tooltip formatter={(v) => formatCurrency(v)} />
                  <Bar dataKey="amount" name="Amount" radius={[4, 4, 0, 0]}>
                    {AGING_DATA.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-lg font-semibold">Collection Methods</CardTitle></CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={COLLECTION_METHODS} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2} dataKey="amount" nameKey="method">
                    {COLLECTION_METHODS.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={['#10b981', '#3b82f6', '#6b7280', '#000'][index]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => formatCurrency(v)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input placeholder="Search patients..." className="pl-10" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} data-testid="billing-search" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40" data-testid="billing-status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="current">Current</SelectItem>
                <SelectItem value="past30">1-30 Days</SelectItem>
                <SelectItem value="past60">31-60 Days</SelectItem>
                <SelectItem value="past90">90+ Days</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Patients Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Patient Balances ({filteredPatients.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Patient Name</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Last Service</TableHead>
                <TableHead className="text-right">Balance</TableHead>
                <TableHead>Age</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPatients.map((patient) => (
                <TableRow key={patient.id} className="hover:bg-slate-50" data-testid={`billing-row-${patient.id}`}>
                  <TableCell className="font-medium">{patient.name}</TableCell>
                  <TableCell>
                    <div className="text-sm">
                      <p className="flex items-center gap-1"><Phone className="h-3 w-3" /> {patient.phone}</p>
                      <p className="flex items-center gap-1 text-slate-500"><Mail className="h-3 w-3" /> {patient.email}</p>
                    </div>
                  </TableCell>
                  <TableCell>{patient.lastService}</TableCell>
                  <TableCell className="text-right font-medium">{formatCurrency(patient.balance)}</TableCell>
                  <TableCell>{getStatusBadge(patient.status)}</TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`billing-menu-${patient.id}`}>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem data-testid={`billing-chart-${patient.id}`} onSelect={() => navigate(`/revq/patients/${patient.id}`)}>
                          <Eye className="h-4 w-4 mr-2" /> View Patient Chart
                        </DropdownMenuItem>
                        <DropdownMenuItem data-testid={`billing-payment-${patient.id}`} onSelect={() => openModal(patient, 'payment')}>
                          <CreditCard className="h-4 w-4 mr-2" /> Process Payment
                        </DropdownMenuItem>
                        <DropdownMenuItem data-testid={`billing-statement-${patient.id}`} onSelect={() => openModal(patient, 'statement')}>
                          <Mail className="h-4 w-4 mr-2" /> Send Statement
                        </DropdownMenuItem>
                        <DropdownMenuItem data-testid={`billing-history-${patient.id}`} onSelect={() => openModal(patient, 'history')}>
                          <FileText className="h-4 w-4 mr-2" /> View History
                        </DropdownMenuItem>
                        <DropdownMenuItem data-testid={`billing-plan-${patient.id}`} onSelect={() => openModal(patient, 'plan')}>
                          <CalendarClock className="h-4 w-4 mr-2" /> Setup Payment Plan
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Process Payment Modal */}
      <Dialog open={modalType === 'payment'} onOpenChange={(open) => !open && closeModal()}>
        <DialogContent className="max-w-md" data-testid="process-payment-modal">
          <DialogHeader>
            <DialogTitle>Process Payment</DialogTitle>
            <DialogDescription>Record a payment for {selectedPatient?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg border bg-slate-50 p-3 flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Outstanding Balance</span>
              <span className="text-lg font-bold">{formatCurrency(selectedPatient?.balance || 0)}</span>
            </div>
            <div className="space-y-2">
              <Label htmlFor="payment-amount">Payment Amount</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input id="payment-amount" data-testid="payment-amount-input" type="number" step="0.01" className="pl-9" placeholder="0.00" value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Payment Method</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger data-testid="payment-method-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="credit_card">Credit Card</SelectItem>
                  <SelectItem value="debit_card">Debit Card</SelectItem>
                  <SelectItem value="check">Check</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="eft">EFT/ACH</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeModal}>Cancel</Button>
            <Button onClick={handleProcessPayment} data-testid="submit-payment-btn" className="bg-emerald-600 hover:bg-emerald-700 gap-2">
              <CheckCircle2 className="h-4 w-4" /> Process Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Statement Modal */}
      <Dialog open={modalType === 'statement'} onOpenChange={(open) => !open && closeModal()}>
        <DialogContent className="max-w-md" data-testid="send-statement-modal">
          <DialogHeader>
            <DialogTitle>Send Statement</DialogTitle>
            <DialogDescription>Send a billing statement to {selectedPatient?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg border bg-slate-50 p-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Patient</span><span className="font-medium">{selectedPatient?.name}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Email</span><span className="font-medium">{selectedPatient?.email}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Balance</span><span className="font-bold">{formatCurrency(selectedPatient?.balance || 0)}</span></div>
            </div>
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
              <p className="text-sm text-blue-700">A statement detailing all charges and payments will be sent to the patient's email address on file.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeModal}>Cancel</Button>
            <Button onClick={handleSendStatement} data-testid="confirm-statement-btn" className="bg-black hover:bg-gray-800 gap-2">
              <Send className="h-4 w-4" /> Send Statement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View History Modal */}
      <Dialog open={modalType === 'history'} onOpenChange={(open) => !open && closeModal()}>
        <DialogContent className="max-w-lg" data-testid="view-history-modal">
          <DialogHeader>
            <DialogTitle>Billing History: {selectedPatient?.name}</DialogTitle>
            <DialogDescription>Recent charges and payments</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <div className="rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Charge</TableHead>
                    <TableHead className="text-right">Payment</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedPatient?.history?.map((item, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-sm">{item.date}</TableCell>
                      <TableCell className="text-sm">{item.desc}</TableCell>
                      <TableCell className="text-right text-sm">{formatCurrency(item.charge)}</TableCell>
                      <TableCell className="text-right text-sm text-emerald-600">{item.payment > 0 ? formatCurrency(item.payment) : '-'}</TableCell>
                      <TableCell className="text-right text-sm font-medium">{formatCurrency(item.balance)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="flex justify-end pt-2">
              <div className="text-sm"><span className="text-muted-foreground">Total Balance: </span><span className="font-bold text-lg">{formatCurrency(selectedPatient?.balance || 0)}</span></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeModal}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Setup Payment Plan Modal */}
      <Dialog open={modalType === 'plan'} onOpenChange={(open) => !open && closeModal()}>
        <DialogContent className="max-w-md" data-testid="payment-plan-modal">
          <DialogHeader>
            <DialogTitle>Setup Payment Plan</DialogTitle>
            <DialogDescription>Create an installment plan for {selectedPatient?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg border bg-slate-50 p-3 flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total Balance</span>
              <span className="text-lg font-bold">{formatCurrency(selectedPatient?.balance || 0)}</span>
            </div>
            <div className="space-y-2">
              <Label>Number of Installments</Label>
              <Select value={planInstallments} onValueChange={setPlanInstallments}>
                <SelectTrigger data-testid="plan-installments-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2">2 Payments</SelectItem>
                  <SelectItem value="3">3 Payments</SelectItem>
                  <SelectItem value="4">4 Payments</SelectItem>
                  <SelectItem value="6">6 Payments</SelectItem>
                  <SelectItem value="12">12 Payments</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Frequency</Label>
              <Select value={planFrequency} onValueChange={setPlanFrequency}>
                <SelectTrigger data-testid="plan-frequency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="biweekly">Bi-weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {selectedPatient && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                <p className="text-sm text-emerald-800 font-medium">Payment Summary</p>
                <p className="text-sm text-emerald-700 mt-1">
                  {planInstallments} {planFrequency} payments of <strong>{formatCurrency(selectedPatient.balance / parseInt(planInstallments))}</strong>
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeModal}>Cancel</Button>
            <Button onClick={handleSetupPlan} data-testid="confirm-plan-btn" className="bg-black hover:bg-gray-800 gap-2">
              <CalendarClock className="h-4 w-4" /> Create Plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RevQPatientBilling;
